import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import WelcomeHeader from '../components/landing/WelcomeHeader';
import CardHeader from './CardHeader';
import TaskBar from '../components/landing/TaskBar';
import { useModuleNavigation } from '../hooks/landing/useModuleNavigation';
import ChatbotButton from '../components/chatbot/ChatbotButton';
import { FileText, Calendar } from 'lucide-react';

// Imports d'images
import dashboardIcon from '../assets/img3d/dashboard.png';
import staffIcon from '../assets/img3d/staff.png';
import motoIcon from '../assets/img3d/moto.png';
import reparationIcon from '../assets/img3d/reparation.png';
import coinsIcon from '../assets/img3d/coins.png';
import stockIcon from '../assets/img3d/stock.png';
import fuelIcon from '../assets/img3d/carburant.png';
import buildingIcon from '../assets/img3d/building.png';
import chatIcon from '../assets/img3d/chat.png';
import profileClientIcon from '../assets/img3d/profileClient.png';
import calendarIcon from '../assets/img3d/calendar.png';
import homeIcon from '../assets/img3d/home.png';
import fileTextIcon from '../assets/img3d/contrat.png';

interface LayoutProps {
  children: React.ReactNode;
  // Configuration dynamique du header
  headerConfig?: {
    icon?: any;
    iconColor?: string;
    title?: string;
    subtitle?: string;
    primaryAction?: string;
    secondaryAction?: string;
    tertiaryAction?: string;
    quaternaryAction?: string;
    onPrimaryAction?: () => void;
    onSecondaryAction?: () => void;
    onTertiaryAction?: () => void;
    onQuaternaryAction?: () => void;
  };
}

const Layout = ({ 
  children, 
  headerConfig
}: LayoutProps) => {
  const location = useLocation();
  const { navigateToTaskBar } = useModuleNavigation();
  
  const formattedTime = useMemo(
    () =>
      new Intl.DateTimeFormat('fr', {
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date()),
    []
  );
  
  // Configuration des modules basée sur l'URL
  const getModuleConfig = (pathname: string): any => {
    const moduleConfigs: any = {
       '/users': {
         icon: staffIcon,
         title: 'Gestion des Utilisateurs',
         subtitle: 'Gérez les utilisateurs et leurs permissions',
         primaryAction: undefined,
         secondaryAction: undefined,
         tertiaryAction: undefined,
         iconColor: "bg-white"
       },

       '/roles': {
         icon: staffIcon,
         title: 'Gestion des Rôles',
         subtitle: 'Gérez les rôles et leurs permissions',
         primaryAction: undefined,
         secondaryAction: undefined,
         tertiaryAction: undefined,
         iconColor: "bg-white"
       },

       '/couriers': {
         icon: staffIcon,
         title: 'Gestion des Coursiers',
         subtitle: 'Gérez les coursiers et leurs assignations',
         primaryAction: undefined,
         secondaryAction: undefined,
         tertiaryAction: undefined,
         iconColor: "bg-white"
       },

       '/motorcycles': {
         icon: motoIcon,
         title: 'Gestion du Parc de Motos',
         subtitle: 'Gérez votre parc de motos et leurs assignations',
         primaryAction: undefined,
         secondaryAction: undefined,
         tertiaryAction: undefined,
         iconColor: "bg-white"
       },

       '/repairs': {
         icon: reparationIcon,
         title: 'Gestion des Réparations',
         subtitle: 'Suivez et gérez toutes les réparations du parc',
         primaryAction: undefined,
         secondaryAction: undefined,
         tertiaryAction: undefined,
         iconColor: "bg-white"
       },

       '/expenses': {
         icon: coinsIcon,
         title: 'Gestion des Dépenses',
         subtitle: 'Suivez et gérez toutes les dépenses du parc',
         primaryAction: undefined,
         secondaryAction: undefined,
         tertiaryAction: undefined,
         iconColor: "bg-white"
       },

       '/finances': {
         icon: coinsIcon,
         title: 'Gestion des Dépenses',
         subtitle: 'Suivez et gérez toutes les dépenses du parc',
         primaryAction: undefined,
         secondaryAction: undefined,
         tertiaryAction: undefined,
         iconColor: "bg-white"
       },

       '/budget': {
         icon: coinsIcon,
         title: 'Vue d\'ensemble du budget',
         subtitle: 'Suivez l\'utilisation du budget du parc',
         primaryAction: undefined,
         secondaryAction: undefined,
         tertiaryAction: undefined,
         iconColor: "bg-white"
       },

       '/suppliers': {
         icon: coinsIcon,
         title: 'Gestion des Fournisseurs',
         subtitle: 'Gérez vos fournisseurs et leurs informations',
         primaryAction: undefined,
         secondaryAction: undefined,
         tertiaryAction: undefined,
         iconColor: "bg-white"
       },

       '/reports': {
         icon: coinsIcon,
         title: 'Rapports Financiers',
         subtitle: 'Générez et consultez les rapports de dépenses',
         primaryAction: undefined,
         secondaryAction: undefined,
         tertiaryAction: undefined,
         iconColor: "bg-white"
       },

       '/inventory': {
         icon: stockIcon,
         title: 'Gestion des Stocks',
         subtitle: 'Gérez les articles en stock et suivez les niveaux',
         primaryAction: undefined,
         secondaryAction: undefined,
         tertiaryAction: undefined,
         iconColor: "bg-white"
       },

      '/fuel': {
        icon: fuelIcon,
         title: 'Gestion de Carburant',
         subtitle: 'Suivez et gérez tous les ravitaillements du parc',
         primaryAction: undefined,
         secondaryAction: undefined,
         tertiaryAction: undefined,
         iconColor: "bg-white"
       },

       '/fuel-refills': {
        icon: fuelIcon,
         title: 'Gestion de Carburant',
         subtitle: 'Suivez et gérez tous les ravitaillements du parc',
         primaryAction: undefined,
         secondaryAction: undefined,
         tertiaryAction: undefined,
         iconColor: "bg-white"
       },

       '/config': {
         icon: buildingIcon,
         title: 'Configuration',
         subtitle: 'Lancez l’Assistant IA puis affinez champs, catégories, formules, workflows et triggers',
         primaryAction: undefined,
         secondaryAction: undefined,
         tertiaryAction: undefined,
         iconColor: "bg-white"
       },

       '/config/assistant': {
         icon: chatIcon,
         title: 'Assistant Configuration IA',
         subtitle: 'Laissez l\'intelligence artificielle configurer votre système automatiquement',
         primaryAction: undefined,
         secondaryAction: undefined,
         tertiaryAction: undefined,
         iconColor: "bg-white"
       },

       // Client routes
       '/client': {
         icon: profileClientIcon,
         title: 'Mon Espace',
         subtitle: 'Suivez vos demandes de contact',
         primaryAction: undefined,
         secondaryAction: undefined,
         tertiaryAction: undefined,
         iconColor: "bg-white"
       },

       '/client/my-requests': {
         icon: fileTextIcon,
         title: 'Mes Demandes',
         subtitle: 'Gérez et suivez toutes vos demandes',
         primaryAction: undefined,
         secondaryAction: undefined,
         tertiaryAction: undefined,
         iconColor: "bg-white"
       },

       '/client/my-visits': {
         icon: calendarIcon,
         title: 'Mes Visites',
         subtitle: 'Consultez vos visites planifiées',
         primaryAction: undefined,
         secondaryAction: undefined,
         tertiaryAction: undefined,
         iconColor: "bg-white"
       },

       '/properties': {
         icon: homeIcon,
         title: 'Rechercher un Bien',
         subtitle: 'Trouvez le bien immobilier qui correspond à vos critères',
         primaryAction: undefined,
         secondaryAction: undefined,
         tertiaryAction: undefined,
         iconColor: "bg-white"
       }
    };

    // Support nested routes by matching the longest base path prefix
    const matchedKey = Object.keys(moduleConfigs)
      .filter((base) => pathname.startsWith(base))
      .sort((a, b) => b.length - a.length)[0];

    if (matchedKey) {
      return (moduleConfigs as any)[matchedKey];
    }

     return (moduleConfigs as any)[pathname] || {
       icon: dashboardIcon,
       title: 'Tableau de bord',
       subtitle: 'Vue d\'ensemble',
       primaryAction: 'Action principale',
       secondaryAction: 'Filtres',
       iconColor: "bg-white"
     };
  };
  
  const cardHeader: any = getModuleConfig(location.pathname);
  
  // Fusionner avec la configuration dynamique si fournie
  const finalHeaderConfig = {
    ...cardHeader,
    ...(headerConfig && {
      icon: headerConfig.icon !== undefined ? headerConfig.icon : cardHeader.icon,
      iconColor: headerConfig.iconColor !== undefined ? headerConfig.iconColor : cardHeader.iconColor,
      title: headerConfig.title || cardHeader.title,
      subtitle: headerConfig.subtitle || cardHeader.subtitle,
      primaryAction: headerConfig.primaryAction !== undefined ? headerConfig.primaryAction : cardHeader.primaryAction,
      secondaryAction: headerConfig.secondaryAction !== undefined ? headerConfig.secondaryAction : cardHeader.secondaryAction,
      tertiaryAction: headerConfig.tertiaryAction !== undefined ? headerConfig.tertiaryAction : cardHeader.tertiaryAction,
      quaternaryAction: headerConfig.quaternaryAction !== undefined ? headerConfig.quaternaryAction : cardHeader.quaternaryAction,
      onPrimaryAction: headerConfig.onPrimaryAction || cardHeader.onPrimaryAction,
      onSecondaryAction: headerConfig.onSecondaryAction || cardHeader.onSecondaryAction,
      onTertiaryAction: headerConfig.onTertiaryAction || cardHeader.onTertiaryAction,
      onQuaternaryAction: headerConfig.onQuaternaryAction || cardHeader.onQuaternaryAction,
    })
  };
  
  // Forcer undefined si explicitement défini dans headerConfig
  if (headerConfig && 'primaryAction' in headerConfig && headerConfig.primaryAction === undefined) {
    finalHeaderConfig.primaryAction = undefined;
  }
  if (headerConfig && 'secondaryAction' in headerConfig && headerConfig.secondaryAction === undefined) {
    finalHeaderConfig.secondaryAction = undefined;
  }
  if (headerConfig && 'tertiaryAction' in headerConfig && headerConfig.tertiaryAction === undefined) {
    finalHeaderConfig.tertiaryAction = undefined;
  }
  if (headerConfig && 'quaternaryAction' in headerConfig && headerConfig.quaternaryAction === undefined) {
    finalHeaderConfig.quaternaryAction = undefined;
  }

  return (
    <div className="min-h-screen bg-[#FAFBFC] relative">
      {/* Contenu principal */}
      <div className="relative z-10">
        {/* Navigation Header */}
        <WelcomeHeader />

        {/* Container principal avec carte structurée */}
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 mt-2">
          {/* Structure en colonnes : TaskBar à gauche, Carte à droite */}
          <div className="flex gap-3 md:gap-6 items-start">
          {/* Wrapper sticky pour TaskBar et ChatbotButton */}
          <div className="flex-shrink-0 sticky top-16 md:top-20 self-start pt-8 md:pt-6">
              <div className="relative">
                {/* Bouton Chatbot - Positionné au-dessus de la TaskBar */}
                <ChatbotButton variant="sidebar" />
                
                {/* TaskBar verticale à gauche */}
                <TaskBar onModuleClick={navigateToTaskBar} />
              </div>
            </div>
            
            {/* Carte principale - Hero card inspirée des images */}
            <div className="flex-1 bg-white rounded-2xl border border-neutral-200 overflow-hidden shadow-sm">
              {/* Header de la carte avec icône, titre, sous-titre et actions */}
              <CardHeader 
                icon={finalHeaderConfig.icon}
                title={finalHeaderConfig.title}
                subtitle={finalHeaderConfig.subtitle}
                primaryAction={finalHeaderConfig.primaryAction}
                secondaryAction={finalHeaderConfig.secondaryAction}
                tertiaryAction={finalHeaderConfig.tertiaryAction}
                quaternaryAction={finalHeaderConfig.quaternaryAction}
                iconColor={finalHeaderConfig.iconColor}
                onPrimaryAction={finalHeaderConfig.onPrimaryAction}
                onSecondaryAction={finalHeaderConfig.onSecondaryAction}
                onTertiaryAction={finalHeaderConfig.onTertiaryAction}
                onQuaternaryAction={finalHeaderConfig.onQuaternaryAction}
              />
              
              {/* Body de la carte - Le contenu sera injecté ici par le composant enfant */}
              <div className="px-8 pb-8 pt-0">
                {children}
              </div>
            </div>
          </div>

          {/* Footer avec timestamp - retiré pour correspondre au design */}
        </div>
      </div>
    </div>
  );
};

export default Layout;